import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { AGENTS, DEPARTMENTS } from '../../utils/agents';
import { useContentStore } from '../../stores/contentStore';
import type { ContentType, ContentChannel } from '../../types/content';

const CONTENT_TYPES: ContentType[] = ['social', 'email', 'blog', 'ad', 'video'];
const CHANNELS: ContentChannel[] = ['instagram', 'tiktok', 'pinterest', 'email', 'blog', 'facebook', 'etsy'];

interface CreateContentModalProps {
  onClose: () => void;
}

export default function CreateContentModal({ onClose }: CreateContentModalProps) {
  const createContent = useContentStore((s) => s.createContent);

  const [title, setTitle] = useState('');
  const [contentType, setContentType] = useState<ContentType>('social');
  const [channel, setChannel] = useState('');
  const [assignedAgent, setAssignedAgent] = useState('');
  const [department, setDepartment] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  function handleAgentChange(slug: string) {
    setAssignedAgent(slug);
    if (slug) {
      const agent = AGENTS.find((a) => a.slug === slug);
      if (agent) {
        setDepartment(agent.department);
      }
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await createContent({
        title: title.trim(),
        content_type: contentType,
        channel: channel || null,
        assigned_agent: assignedAgent || null,
        department: department || null,
        due_date: dueDate || null,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create content');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={handleClose} />
      <div className="relative bg-bg-secondary border border-border-subtle rounded-xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-text-primary">New Content</h2>
          <button
            onClick={handleClose}
            className="p-1 rounded hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              placeholder="Content title"
              autoFocus
            />
            {error ? <p className="text-error text-xs mt-1">{error}</p> : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Content Type</label>
              <select
                value={contentType}
                onChange={(e) => setContentType(e.target.value as ContentType)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                {CONTENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Channel</label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {c.charAt(0).toUpperCase() + c.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
              >
                <option value="">None</option>
                {DEPARTMENTS.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-text-secondary mb-1">Due Date</label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Assigned Agent</label>
            <select
              value={assignedAgent}
              onChange={(e) => handleAgentChange(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm"
            >
              <option value="">Unassigned</option>
              {AGENTS.map((a) => (
                <option key={a.slug} value={a.slug}>
                  {a.name} -- {a.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-bg-tertiary border border-border-subtle text-text-primary rounded-md px-3 py-2 w-full text-sm focus:outline-none focus:border-accent-purple resize-none"
              placeholder="Optional notes"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <button
              type="button"
              onClick={handleClose}
              className="text-text-muted hover:text-text-secondary text-sm transition-colors px-3 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="bg-accent-purple text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-accent-purple/80 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Content'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
